import React from 'react';
import { useIntl } from 'react-intl';

import Avatar from '@atlaskit/avatar/avatar';
import { cssMap } from '@atlaskit/css';
import { Anchor } from '@atlaskit/primitives/compiled/anchor';
import { Flex } from '@atlaskit/primitives/compiled/flex';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import { Text } from '@atlaskit/primitives/compiled/text';
import { token } from '@atlaskit/tokens';

import type { NestedAdfRendererWrapper } from '@confluence/macro-utils/entry-points/nestedAdfRenderer.ts';
import { ProfileCardWrapper } from '@confluence/profile/entry-points/ProfileCardWrapper.ts';
import { ProfileLink } from '@confluence/profile/entry-points/ProfileLinkComponent.ts';

import { i18n } from '../blogPostsMacroi18n.ts';
import {
	getAuthorAccountId,
	getAuthorAvatarUrl,
	getAuthorName,
	getContentUrl,
	getCreatedDate,
	getLabels,
	formatBlogDate,
} from '../utils/blogPostDataUtils.ts';
import type { BlogPostContentMode, BlogPostNode } from '../blogPostMacroTypes.ts';

import { BlogPostRichContent } from './BlogPostRichContent.tsx';
import { LabelsList } from './LabelsList.tsx';

const styles = cssMap({
	excerptListingContainer: {
		paddingBlock: token('space.300'),
		borderBlockEnd: `${token('border.width')} solid ${token('color.border')}`,
	},
	excerptTitle: {
		font: token('font.heading.large'),
		marginBlockEnd: token('space.025'),
	},
	excerptTitleLink: {
		textDecoration: 'none',
		'&:hover': {
			textDecoration: 'underline',
		},
	},
	excerptMeta: {
		marginBlockEnd: token('space.100'),
		marginBlockStart: token('space.025'),
		display: 'flex',
		alignItems: 'center',
		gap: token('space.050'),
		flexWrap: 'wrap',
	},
});

type ExcerptsListProps = {
	nodes: readonly BlogPostNode[];
	contentMode: BlogPostContentMode;
	RendererWrapper: NestedAdfRendererWrapper;
};

export const ExcerptsList = ({
	nodes,
	contentMode,
	RendererWrapper,
}: ExcerptsListProps): React.JSX.Element => {
	const intl = useIntl();

	return (
		<div data-testid="blog-posts-excerpts-list">
			{nodes.map((node) => {
				const content = node.content;
				if (!content) {
					return null;
				}

				const createdDate = getCreatedDate(node);
				const avatarUrl = getAuthorAvatarUrl(node);
				const authorName = getAuthorName(node);
				const authorAccountId = getAuthorAccountId(node);
				const labels = getLabels(node);
				const spaceKey = content.space?.key;

				return (
					<div key={content.id} css={styles.excerptListingContainer} data-testid="blog-post-item">
						<Stack space="space.100">
							<Flex gap="space.200" alignItems="start">
								<ProfileCardWrapper
									userId={authorAccountId}
									fullName={authorName}
									position="bottom-start"
								>
									<Avatar src={avatarUrl} size="large" name={authorName} />
								</ProfileCardWrapper>
								<div>
									<div css={styles.excerptTitle}>
										<Anchor href={getContentUrl(node)} xcss={styles.excerptTitleLink}>
											{content.title}
										</Anchor>
									</div>
									<div css={styles.excerptMeta}>
										<Inline space="space.050" alignBlock="baseline">
											<ProfileCardWrapper
												userId={authorAccountId}
												fullName={authorName}
												position="bottom-start"
											>
												<ProfileLink
													userId={authorAccountId}
													fullName={authorName}
													behavior="link"
													isUnderlinedByDefault={false}
													style={{
														// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- style overrides to match legacy macro subtle link styling
														font: token('font.body.small'),
													}}
												/>
											</ProfileCardWrapper>
											{Boolean(createdDate) && (
												<Text color="color.text.subtlest" size="small">
													{intl.formatMessage(i18n.excerptPostedOn, {
														date: formatBlogDate(createdDate, intl),
													})}
												</Text>
											)}
										</Inline>
									</div>
								</div>
							</Flex>
							{contentMode === 'entire' && (
								<BlogPostRichContent
									atlasDocFormat={content.body?.atlas_doc_format?.value}
									mediaToken={content.body?.atlas_doc_format?.mediaToken?.token}
									contentId={content.id ?? ''}
									spaceKey={content.space?.key ?? ''}
									spaceId={content.space?.id ?? undefined}
									RendererWrapper={RendererWrapper}
								/>
							)}
							{contentMode === 'excerpts' && (
								<BlogPostRichContent
									atlasDocFormat={content.macroExcerpt?.atlasDocFormat}
									mediaToken={content.body?.atlas_doc_format?.mediaToken?.token}
									contentId={content.id ?? ''}
									spaceKey={content.space?.key ?? ''}
									spaceId={content.space?.id ?? undefined}
									RendererWrapper={RendererWrapper}
									fallback={content.excerpt ? <Text>{content.excerpt}</Text> : null}
								/>
							)}
							{labels.length > 0 && <LabelsList labels={labels} spaceKey={spaceKey} />}
						</Stack>
					</div>
				);
			})}
		</div>
	);
};
